/**
 * Le catalogue public, sans savoir d'où viennent ses données.
 *
 * Même raison d'être que `StageVue` : ce composant est rendu par le navigateur
 * ET au build, par `renderToString`. Il ne lit rien et n'a pas d'état, ce qui
 * est la condition pour que la page pré-rendue et la page vécue disent la même
 * chose.
 *
 * ## Une carte par activité, et non par séance
 *
 * La première version listait les SÉANCES : 67 lignes de texte en production
 * pour 10 activités, c'est-à-dire « Les Aigles 9 - 13 ans » dix fois de suite,
 * même photo, même description, seule la commune changeant. Écrite pour qu'un
 * robot reparte avec toutes les adresses, elle y parvenait — et noyait le
 * lecteur humain, qui devait lire dix fois la même chose avant d'atteindre
 * l'activité suivante.
 *
 * Elle liste maintenant les activités, chacune portant ses séances en liens.
 * Les 67 adresses sont toujours là, une par séance, mais rangées sous
 * l'activité dont elles relèvent : c'est l'ordre dans lequel une famille
 * décide — d'abord « est-ce que vous faites du vélo ? », ensuite seulement
 * « où et quand ? ».
 *
 * ## Pourquoi on ne cache rien aux visiteurs
 *
 * Servir un contenu aux moteurs et un autre aux humains porte un nom,
 * « cloaking », et se paie d'un déclassement. Cela casserait de surcroît
 * l'usage même qu'on vise : un assistant qui répond à une famille récupère la
 * page que cette famille verrait. Les faits restent donc écrits, visibles et
 * identiques pour tous ; c'est leur MISE EN PAGE qui change — une vignette, un
 * titre, trois faits comparables d'un coup d'œil — au lieu d'un paragraphe.
 */

import { ORGANISATEUR, type StagePublic } from '../../lib/seo/stage';
import { activitesParType, communesDesStages } from '../../lib/seo/catalogue';
import CarteDActivite from './CarteDActivite';

interface Props {
  stages: StagePublic[];
}

const CatalogueVue = ({ stages }: Props) => {
  const sections = activitesParType(stages);
  const communes = communesDesStages(stages);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-3">
        Stages et plaines de vacances pour enfants à Bruxelles
      </h1>
      <p className="text-gray-700 mb-2">
        {ORGANISATEUR} organise des stages et des plaines de vacances pour enfants dans
        plusieurs communes bruxelloises, pendant les congés scolaires. Chaque activité
        indique son lieu, ses dates, la tranche d’âge accueillie et ses tarifs.
      </p>
      {communes.length > 0 && (
        <p className="text-gray-700 mb-8">
          Communes concernées actuellement : {communes.join(', ')}.
        </p>
      )}

      {sections.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-700">
            Aucun stage n’est ouvert aux inscriptions en ce moment. Les prochaines dates
            sont publiées ici dès leur ouverture.
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.type}>
              {/*
                L'intertitre nomme le type. « plaine de vacances Bruxelles » et
                « stage enfant Bruxelles » sont deux recherches distinctes, et
                une page qui n'écrit ni l'une ni l'autre ne répond à aucune.
              */}
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{section.titre}</h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {section.activites.map((a) => (
                  <CarteDActivite key={a.nom} activite={a} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default CatalogueVue;
